import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter } from 'rxjs';
import { AuthService, UserGroup } from '../../services/auth.service';
import { SearchService } from '../../services/search.service';

@Component({
  selector: 'app-user-groups',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './user-groups.component.html',
  styleUrl: './user-groups.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserGroupsComponent implements OnInit {
  public authService = inject(AuthService);
  private readonly searchService = inject(SearchService);
  private readonly router = inject(Router);

  public groups$ = this.authService.groups$;
  public selectedGroupId = signal<string>('');
  public expandedMap = signal<Record<string, boolean>>({});

  ngOnInit() {
    this.updateSelectedGroupFromRoute();
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateSelectedGroupFromRoute();
      });

    this.authService.groups$.subscribe((groups) => {
      if (!this.selectedGroupId() && groups.length > 0) {
        this.updateSelectedGroupFromRoute();
      }
    });
  }

  private updateSelectedGroupFromRoute() {
    let currentRoute: ActivatedRoute | null = this.router.routerState.root;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }
    const idx = currentRoute.snapshot.queryParams['index'];
    if (idx) {
      this.selectedGroupId.set(idx);
    } else {
      const groups = this.authService.getUserGroups();
      if (groups.length > 0) {
        this.selectedGroupId.set(groups[0].id);
      }
    }
  }

  selectGroup(group: UserGroup) {
    this.selectedGroupId.set(group.id);
    this.expandedMap.update((map) => ({ ...map, [group.id]: true }));
    this.searchService.resetChat(group.id);
  }

  startNewChat(groupId?: string, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    if (groupId) {
      this.selectedGroupId.set(groupId);
      this.expandedMap.update((map) => ({ ...map, [groupId]: true }));
    }
    this.searchService.resetChat(groupId);
  }

  isExpanded(groupId: string): boolean {
    const map = this.expandedMap();
    if (map[groupId] !== undefined) {
      return map[groupId];
    }
    return this.selectedGroupId() === groupId;
  }

  toggleExpand(groupId: string, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    const current = this.isExpanded(groupId);
    this.expandedMap.update((map) => ({
      ...map,
      [groupId]: !current,
    }));
  }

  isGroupActive(groupId: string): boolean {
    return this.selectedGroupId() === groupId && this.router.url.startsWith('/search');
  }

  isItemActive(path: string, groupId?: string): boolean {
    if (groupId) {
      return this.selectedGroupId() === groupId && this.router.url.startsWith(path);
    }
    return this.router.url.startsWith(path);
  }
}
